import { Link } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

export function CheckoutPage() {
  const { isAuthenticated } = useAuthStore();
  const { items, getSubtotal, getTotal, discountAmount } = useCartStore();

  const subtotal = getSubtotal();
  const total = getTotal();
  const tax = (subtotal - discountAmount) * 0.0825;

  if (items.length === 0) {
    return (
      <div className="container-narrow py-16">
        <EmptyState
          icon={
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          }
          title="Your cart is empty"
          description="Add some items to your cart to checkout."
          action={
            <Link to="/shop">
              <Button variant="sage">Browse Shop</Button>
            </Link>
          }
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container-narrow py-16 text-center">
        <h1 className="text-2xl font-bold text-sand-900">Sign in to checkout</h1>
        <p className="mt-4 text-sand-500">You need to be signed in to complete your purchase.</p>
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/login?redirect=/checkout">
            <Button variant="primary">Sign In</Button>
          </Link>
          <Link to="/register?redirect=/checkout">
            <Button variant="outline">Create Account</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        <h1 className="text-3xl font-bold text-sand-900 mb-8">Checkout</h1>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-lg font-semibold text-sand-900 mb-6">Payment</h2>

              {/* Square Payment Form would go here */}
              <div className="rounded-xl bg-sand-50 p-8 text-center">
                <svg className="h-12 w-12 text-sand-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <p className="text-sand-600 mb-2">Square Payment Integration</p>
                <p className="text-sm text-sand-500">
                  Configure your Square credentials to enable payments.
                </p>
              </div>

              <Button variant="sage" className="w-full mt-6" disabled>
                Pay {formatCurrency(total)}
              </Button>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <h2 className="text-lg font-semibold text-sand-900 mb-4">Order Summary</h2>

              {/* Items */}
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={`${item.productId}-${item.variantId}`} className="flex justify-between text-sm">
                    <span className="text-sand-600">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="text-sand-900">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <hr className="border-sand-200 mb-4" />

              <div className="space-y-3">
                <div className="flex justify-between text-sand-700">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sage-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sand-700">
                  <span>Tax</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
                <hr className="border-sand-200" />
                <div className="flex justify-between text-lg font-semibold text-sand-900">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
